/**
 * Spell check nativo del browser (Opzione A) sui campi di testo estesi dell'app.
 *
 * Attiva `spellcheck="true"` e imposta `lang` su ogni <textarea> e area
 * contenteditable (incluso il content del Syncfusion RichTextEditor e di
 * CKEditor). L'obiettivo è il controllo ortografico in tempo reale offerto
 * gratuitamente dal browser, nella lingua CORRENTEMENTE impostata dall'utente.
 *
 * Perché serve impostarli esplicitamente:
 *  - `spellcheck="true"`: sebbene sia il default ereditato, renderlo esplicito
 *    blinda contro eventuali antenati che lo disattivano e chiarisce l'intento.
 *  - `lang`: fornisce al browser l'hint di lingua = quella della pagina, letta
 *    da <html lang="..."> che i layout server valorizzano da
 *    CultureInfo.CurrentUICulture (it-IT / en-US / ...). Vedi _Layout.cshtml.
 *
 * Limite intrinseco (non aggirabile lato app): il dizionario effettivamente
 * usato dipende dal browser dell'utente. Firefox onora l'attributo `lang`;
 * Chrome/Edge usano prevalentemente le lingue configurate dall'utente in
 * impostazioni e il dizionario italiano dev'essere attivo lì. L'app fornisce
 * l'hint, ma non può installare/forzare il dizionario.
 *
 * Opt-out: gli elementi con un attributo `spellcheck` GIÀ esplicito (es. gli
 * input URL dei dialog Syncfusion, con spellcheck="false") NON vengono toccati.
 */

const PROCESSED_ATTR = 'data-spellcheck-init';
const TARGET_SELECTOR = 'textarea, [contenteditable]:not([contenteditable="false"])';

/** Lingua corrente della pagina (BCP-47), da <html lang>. Fallback: it-IT. */
function pageLang(): string {
    const l = document.documentElement.getAttribute('lang');
    return l && l.trim() ? l.trim() : 'it-IT';
}

/** Applica spellcheck + lang a un singolo elemento, una sola volta. */
function apply(el: HTMLElement, lang: string): void {
    if (el.hasAttribute(PROCESSED_ATTR))
        return;
    el.setAttribute(PROCESSED_ATTR, '');

    // Non sovrascrivere un opt-out (o opt-in) esplicito già presente.
    if (!el.hasAttribute('spellcheck'))
        el.setAttribute('spellcheck', 'true');

    // Hint di lingua per-campo, senza sovrascrivere un lang già impostato.
    if (!el.hasAttribute('lang'))
        el.setAttribute('lang', lang);
}

/** Scandisce un sottoalbero e applica a tutti i target trovati. */
function sweep(root: ParentNode, lang: string): void {
    root.querySelectorAll<HTMLElement>(TARGET_SELECTOR).forEach(n => apply(n, lang));
}

/**
 * Inizializza lo spell check globale: sweep iniziale + MutationObserver per gli
 * editor creati dinamicamente (dialog, grid inline, RTE aperti a runtime).
 * Idempotente: chiamate multiple non creano observer duplicati.
 */
export function initSpellCheck(): void {
    if (typeof document === 'undefined' || (window as any).__spellCheckInit)
        return;
    (window as any).__spellCheckInit = true;

    const run = () => {
        const lang = pageLang();
        sweep(document, lang);

        const observer = new MutationObserver(mutations => {
            for (const m of mutations) {
                m.addedNodes.forEach(node => {
                    if (!(node instanceof HTMLElement))
                        return;
                    if (node.matches(TARGET_SELECTOR))
                        apply(node, lang);
                    sweep(node, lang);
                });
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading')
        document.addEventListener('DOMContentLoaded', run, { once: true });
    else
        run();
}
